import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};

const STORAGE_KEY = "mplaisir_cart_v1";

// Safe storage helpers — some browsers block localStorage (private mode,
// strict privacy, extensions). Never let storage errors crash the app.
const safeGet = (key) => {
  try {
    return typeof window !== "undefined" && window.localStorage
      ? window.localStorage.getItem(key)
      : null;
  } catch (e) {
    console.warn("localStorage indisponible (lecture):", e?.message || e);
    return null;
  }
};
const safeSet = (key, value) => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(key, value);
    }
  } catch (e) {
    // storage unavailable — cart just won't persist
    console.warn("localStorage indisponible (écriture):", e?.message || e);
  }
};

// Ensure loaded cart lines have the fields the UI expects (avoid render crashes)
const sanitize = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((l) => l && typeof l === "object")
    .map((l, i) => ({
      ...l,
      name: typeof l.name === "string" ? l.name : "Article",
      unitPrice: Number.isFinite(l.unitPrice) ? l.unitPrice : 0,
      qty: Number.isFinite(l.qty) && l.qty > 0 ? l.qty : 1,
      meats: Array.isArray(l.meats) ? l.meats : [],
      sauces: Array.isArray(l.sauces) ? l.sauces : [],
      lineId: l.lineId || `restored-${i}-${Math.random().toString(36).slice(2, 7)}`,
    }));
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = safeGet(STORAGE_KEY);
      return raw ? sanitize(JSON.parse(raw)) : [];
    } catch (e) {
      console.warn("Panier local illisible, réinitialisé:", e?.message || e);
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    safeSet(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (line) => {
    setItems((prev) => {
      // merge identical config
      const key = JSON.stringify({ id: line.id, format: line.format, meats: line.meats, sauces: line.sauces, crudites: line.crudites, support: line.support, boisson: line.boisson });
      const idx = prev.findIndex((p) => p._key === key);
      if (idx >= 0) {
        const clone = [...prev];
        clone[idx] = { ...clone[idx], qty: clone[idx].qty + line.qty };
        return clone;
      }
      return [...prev, { ...line, _key: key, lineId: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` }];
    });
    setOpen(true);
  };

  const removeItem = (lineId) => setItems((prev) => prev.filter((p) => p.lineId !== lineId));
  const updateQty = (lineId, qty) =>
    setItems((prev) => prev.map((p) => (p.lineId === lineId ? { ...p, qty: Math.max(1, qty) } : p)));
  const clear = () => setItems([]);

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.unitPrice * i.qty, 0), [items]);

  const value = { items, addItem, removeItem, updateQty, clear, count, subtotal, open, setOpen };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
